import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Button, Empty, Grid, Modal } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AxiosUtils from 'utils/axios-utils';
import { colors } from 'theme/tokens';
import { PersonAvatar, Pill } from 'pages/support-shared/components';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';
import {
  Breakable,
  ReasonInput,
  apiErrorText,
  isMissingEndpoint,
  readableText,
  reasonOk,
  unwrapPage,
} from 'pages/access-groups/common';
import {
  ShiftOption,
  StaffAccessSummary,
  StaffBulkResult,
  StaffClaimStatus,
  StaffTier,
  StaffUserBrief,
} from './types';
import styles from './styles.module.scss';

// ------------------------------------------------------------------ lỗi

export const STAFF_ERRORS: Record<string, string> = {
  STAFF_CLAIM_NOT_FOUND: 'Không tìm thấy yêu cầu này. Có thể người gửi đã gửi yêu cầu mới.',
  STAFF_CLAIM_NOT_PENDING: 'Yêu cầu này đã được xử lý trước đó.',
  STAFF_USER_NOT_VERIFIED: 'Người này chưa xác minh email nên chưa duyệt được.',
  STAFF_NOTE_REQUIRED: 'Cần nhập lý do.',
  STAFF_ACCESS_UNAVAILABLE:
    'Bảng dữ liệu chưa được tạo trên máy chủ nên chưa làm được việc này. Hãy báo người vận hành máy chủ.',
  STAFF_REVOKE_PRIVILEGED:
    'Không gỡ Admin hoặc Quản lý bằng cách này. Hãy đổi phòng ban của họ ở trang Tài khoản.',
  STAFF_CLAIM_PRIVILEGED:
    'Người này là Admin hoặc Quản lý, hoặc yêu cầu vào / ra phòng QL Vận Hành (phòng quyết định cấp duyệt): không duyệt ở đây. Hãy đổi phòng ban của họ ở trang Tài khoản.',
  // Máy chủ thêm ngoài hợp đồng (hp-media-server-staging/src/staff-access/staff-access.errors.ts).
  STAFF_USER_NOT_FOUND: 'Không tìm thấy tài khoản này.',
  STAFF_USER_NOT_STAFF: 'Người này hiện không còn là nhân viên. Tải lại danh sách.',
  STAFF_SHIFT_INVALID:
    'Ca làm không hợp lệ: phải là ca đang chạy của đúng phòng ban. Chọn ca khác rồi thử lại.',
  STAFF_BULK_INVALID: 'Danh sách chọn không hợp lệ: mỗi lần từ 1 đến 50 người.',
  STAFF_MODE_INVALID: 'Chế độ không hợp lệ. Tải lại trang rồi thử lại.',
  STAFF_PARAM_INVALID: 'Dữ liệu gửi lên không hợp lệ. Tải lại trang rồi thử lại.',
};

/**
 * Câu lỗi: ưu tiên câu tiếng Việt máy chủ gửi ở `error` (nói đúng chuyện đã xảy ra, vd. "Lỗi máy
 * chủ…" khác "chưa có bảng"), không có thì mới dùng bảng mã ở trên.
 */
export const staffError = (err: any, fallback?: string) =>
  readableText(err?.error) || apiErrorText(err, STAFF_ERRORS, fallback);

/** Câu lỗi của từng dòng trong kết quả hàng loạt: ưu tiên câu máy chủ gửi, rồi tới bảng mã. */
export const failureText = (failure: { code?: string; text?: string }) =>
  readableText(failure.text) ||
  (failure.code && STAFF_ERRORS[failure.code]) ||
  'Không làm được, vui lòng thử lại.';

// ------------------------------------------------------------------ nhãn

export const TIER_LABEL: Record<StaffTier, string> = {
  ADMIN: 'Admin',
  SMOD: 'Quản lý tổng (SMOD)',
  OPS_MANAGER: 'Quản lý vận hành',
};

export const SOURCE_LABEL: Record<string, string> = {
  SIGNUP: 'Đăng ký',
  PROFILE: 'Hồ sơ',
};

/** Trạng thái một yêu cầu (staff_claims.status). */
export const CLAIM_STATUS_LABEL: Record<StaffClaimStatus, string> = {
  PENDING: 'Chờ duyệt',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Đã từ chối',
  SUPERSEDED: 'Đã thay bằng yêu cầu mới',
  APPLIED: 'Đã ghi thẳng (chế độ cũ)',
};

// Tên vai trò tài khoản (không có cấp duyệt). Cách gọi đã chốt: Creator viết hoa, không dùng "Idol".
const ROLE_TEXT: Record<string, string> = {
  AGENCY: 'Agency',
  CREATOR: 'Creator Online',
  STUDIO_CREATOR: 'Creator Studio',
  USER: 'người dùng thường',
};

const rolesOf = (roles?: string | string[] | null): string[] =>
  (Array.isArray(roles) ? roles : String(roles || '').split(','))
    .map((role) => role.trim().toUpperCase())
    .filter(Boolean);

/**
 * Cấp duyệt hiện tại, cùng luật với máy chủ (approvals/core/approval-tier.ts): có ADMIN → Admin ·
 * có MOD và phòng "QL Vận Hành" (MANAGER) → Quản lý vận hành · MOD phòng khác → Quản lý tổng (SMOD).
 */
export function tierOfUser(user?: StaffUserBrief | null): StaffTier | null {
  const roles = rolesOf(user?.roles);
  if (roles.includes('ADMIN')) return 'ADMIN';
  if (!roles.includes('MOD')) return null;
  const department = String(user?.department || '').trim().toUpperCase();
  return department === 'MANAGER' ? 'OPS_MANAGER' : 'SMOD';
}

/**
 * Dòng "Vai trò hiện tại" trong ô Người: cấp duyệt nếu có (chữ tím đậm cho dễ thấy), không thì
 * vai trò tài khoản. Là dòng chữ (không phải nhãn một dòng) để tên dài tự xuống dòng trong ô hẹp.
 * Có phòng ban hoặc cờ "thuộc HP" là Nhân viên (cùng luật isStaffFlags của máy chủ), kèm tên phòng;
 * "người dùng thường" chỉ khi không có gì khác.
 */
export const RoleLine = ({ user }: { user: StaffUserBrief }) => {
  const tier = tierOfUser(user);
  const department = departmentText(user.department, user.departmentLabel);
  const staff =
    !!user.department || !!Number(user.isHpCompany)
      ? [`Nhân viên${department ? ` (${department})` : ''}`]
      : [];
  const roles = [
    ...staff,
    ...rolesOf(user.roles)
      .filter((role) => role !== 'USER')
      .map((role) => ROLE_TEXT[role] || role),
  ];
  return (
    <span className={styles.personSub}>
      Vai trò hiện tại:{' '}
      {tier ? (
        <strong style={{ color: colors.purpleInk }}>{TIER_LABEL[tier]}</strong>
      ) : (
        <span style={{ color: colors.textPrimary }}>
          {roles.length ? roles.join(', ') : ROLE_TEXT.USER}
        </span>
      )}
    </span>
  );
};

export const departmentText = (code?: string | null, label?: string | null) =>
  label || (code ? DEPARTMENT_LABEL[code] || code : '');

export const personName = (user?: StaffUserBrief | null) =>
  user?.nickName || user?.username || user?.email || (user?.id ? `Tài khoản số ${user.id}` : '—');

/** `defaultShift` là mã ca dạng chuỗi; đổi sang tên ca cho dễ đọc. */
export function shiftText(value: string | null | undefined, shifts: ShiftOption[]): string {
  if (!value) return '';
  const found = shifts.find((shift) => String(shift.id) === String(value));
  if (found) return found.name;
  return /^\d+$/.test(value) ? `Ca số ${value}` : value;
}

// ------------------------------------------------------------------ ngữ cảnh trang

export interface StaffCtx {
  summary: StaffAccessSummary;
  shifts: ShiftOption[];
  /** Tăng sau mỗi thao tác → các bảng tải lại. */
  refreshKey: number;
  afterAction: () => void;
  applySummary: (next: StaffAccessSummary) => void;
  settingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
}

export const StaffContext = createContext<StaffCtx | null>(null);

export function useStaff(): StaffCtx {
  const ctx = useContext(StaffContext);
  if (!ctx) throw new Error('useStaff phải nằm trong StaffContext.Provider');
  return ctx;
}

/** Màn hẹp (điện thoại): bỏ cột ghim phải để bảng còn chỗ cuộn. */
export function useIsNarrow(): boolean {
  const screens = Grid.useBreakpoint();
  return screens.md === false;
}

// ------------------------------------------------------------------ tải danh sách

interface ListState<T> {
  items: T[];
  total: number;
  loading: boolean;
  error: string;
  missing: boolean;
}

/** Danh sách phân trang của trang này; lỗi hiện ngay trong khung bảng kèm nút Thử lại. */
export function useStaffList<T>(url: string, params: Record<string, any>, refreshKey: number) {
  const [state, setState] = useState<ListState<T>>({
    items: [],
    total: 0,
    loading: true,
    error: '',
    missing: false,
  });
  const [tick, setTick] = useState(0);
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    const query: Record<string, any> = {};
    const parsed = JSON.parse(paramsKey);
    Object.keys(parsed).forEach((key) => {
      if (parsed[key] !== '' && parsed[key] !== null && parsed[key] !== undefined) {
        query[key] = parsed[key];
      }
    });
    AxiosUtils.get(url, query)
      .then((res: any) => {
        if (cancelled) return;
        const next = unwrapPage<T>(res);
        setState({ ...next, loading: false, error: '', missing: false });
      })
      .catch((err) => {
        if (cancelled) return;
        const missing = isMissingEndpoint(err);
        setState({
          items: [],
          total: 0,
          loading: false,
          missing,
          error: missing
            ? 'Phần máy chủ của danh sách này đang được bật. Quay lại sau ít phút.'
            : staffError(err, 'Không tải được danh sách. Vui lòng thử lại.'),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [url, paramsKey, refreshKey, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { ...state, reload };
}

export const ListError = ({ text, onRetry }: { text: string; onRetry: () => void }) => (
  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={text} style={{ padding: '32px 0' }}>
    <Button icon={<ReloadOutlined />} onClick={onRetry}>
      Thử lại
    </Button>
  </Empty>
);

export const EmptyHint = ({ title, text }: { title: string; text: string }) => (
  <Empty
    image={Empty.PRESENTED_IMAGE_SIMPLE}
    description={
      <span>
        <strong style={{ display: 'block', color: colors.textPrimary }}>{title}</strong>
        <span style={{ color: colors.textSecondary }}>{text}</span>
      </span>
    }
  />
);

// ------------------------------------------------------------------ ô "Người"

export const PersonCell = ({
  user,
  extra,
  showRole,
}: {
  user: StaffUserBrief;
  extra?: string;
  /** Thêm dòng "Vai trò hiện tại" (thẻ Chờ duyệt: duyệt có thể đổi cấp duyệt của Admin / Quản lý). */
  showRole?: boolean;
}) => {
  // Email và TikTok ID mỗi thứ một dòng: dữ liệu thật có email ~32 ký tự, ghép chung là tràn ô.
  return (
    <div className={styles.person}>
      <PersonAvatar name={personName(user)} avatar={user.avatar} size={36} />
      <div className={styles.personText}>
        <span className={styles.personName}>{personName(user)}</span>
        {user.email && (
          <span className={styles.personSub}>
            <Breakable text={user.email} />
          </span>
        )}
        {user.username && (
          <span className={styles.personSub}>
            <Breakable text={`@${user.username}`} />
          </span>
        )}
        {extra && <span className={styles.personSub}>{extra}</span>}
        {showRole && <RoleLine user={user} />}
        <span className={styles.personTags}>
          {user.isVerifyAccount ? (
            <Pill color={colors.successInk} bg={colors.successSoft}>
              Đã xác minh email
            </Pill>
          ) : (
            <Pill color={colors.warningInk} bg={colors.warningSoft}>
              Chưa xác minh email
            </Pill>
          )}
          {user.isAccountDisabled && (
            <Pill color={colors.dangerInk} bg={colors.dangerSoft}>
              Tài khoản đang khoá
            </Pill>
          )}
        </span>
      </div>
    </div>
  );
};

/** Ngày trên một dòng, giờ dòng dưới: cột chỉ cần ~120px thay vì 160px. */
export const DateTimeCell = ({ value }: { value?: string | null }) => {
  if (!value || !dayjs(value).isValid()) return <span className={styles.sub}>—</span>;
  return (
    <span className={styles.nowrap}>
      {dayjs(value).format('DD/MM/YYYY')}
      <span className={styles.sub} style={{ display: 'block' }}>
        {dayjs(value).format('HH:mm')}
      </span>
    </span>
  );
};

// ------------------------------------------------------------------ hộp xác nhận có ghi chú

export interface ActionConfig {
  title: string;
  okText: string;
  danger?: boolean;
  description?: React.ReactNode;
  noteLabel: string;
  /** 0 = ghi chú không bắt buộc. */
  noteMin: number;
  notePlaceholder?: string;
  /** Ném lỗi thì hộp giữ nguyên và hiện câu lỗi để thử lại. */
  onSubmit: (note: string) => Promise<void>;
}

export const ActionModal = ({
  config,
  extra,
  onClose,
}: {
  config: ActionConfig | null;
  /**
   * Phần chọn thêm (vd. ca làm) nằm trên ô ghi chú. Truyền riêng, không gộp vào `config`:
   * ô ghi chú được xoá mỗi khi `config` đổi, nên `config` phải giữ nguyên trong lúc hộp mở.
   */
  extra?: React.ReactNode;
  onClose: () => void;
}) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setNote('');
    setError('');
  }, [config]);

  if (!config) return null;
  const valid = config.noteMin <= 0 || reasonOk(note, config.noteMin);

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await config.onSubmit(note.trim());
      onClose();
    } catch (err) {
      setError(staffError(err, 'Chưa làm được. Vui lòng thử lại.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      width={560}
      title={<span className={styles.modalTitle}>{config.title}</span>}
      okText={config.okText}
      cancelText="Huỷ"
      okButtonProps={{ danger: config.danger, disabled: !valid }}
      confirmLoading={saving}
      maskClosable={false}
      onCancel={saving ? undefined : onClose}
      onOk={submit}
      destroyOnHidden
    >
      {config.description}
      {extra}
      <ReasonInput
        id="staff-action-note"
        label={config.noteLabel}
        min={config.noteMin}
        value={note}
        onChange={setNote}
        disabled={saving}
        placeholder={config.notePlaceholder}
      />
      {error && <Alert type="error" showIcon style={{ marginTop: 8 }} message={error} />}
    </Modal>
  );
};

/** Danh sách tên trong hộp hàng loạt: tối đa 20 tên, còn lại ghi "và n người nữa". */
export const NameList = ({ names }: { names: string[] }) => {
  const shown = names.slice(0, 20);
  const rest = names.length - shown.length;
  return (
    <div className={styles.names}>
      {shown.join(', ')}
      {rest > 0 ? ` và ${rest} người nữa` : ''}
    </div>
  );
};

/**
 * Thân phản hồi của việc hàng loạt `{ ok, failed, warnings? }` (chấp nhận cả dạng bọc `{ data }`).
 * Máy chủ cũ không gửi `warnings` → mảng rỗng.
 */
export function readBulk(res: any): StaffBulkResult {
  const body = Array.isArray(res?.data?.ok) ? res.data : res?.data?.data;
  return {
    ok: Array.isArray(body?.ok) ? body.ok : [],
    failed: Array.isArray(body?.failed) ? body.failed : [],
    warnings: Array.isArray(body?.warnings) ? body.warnings : [],
  };
}

/** Câu cảnh báo của một yêu cầu vừa duyệt (`StaffClaimRow.warning`); không có thì chuỗi rỗng. */
export const warningText = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

// ------------------------------------------------------------------ không duyệt được ở thẻ Chờ duyệt

/**
 * Máy chủ đánh dấu `privileged` cho yêu cầu của Admin, Quản lý (MOD), và của người thường xin vào
 * hoặc đang ở phòng "QL Vận Hành" (phòng quyết định cấp duyệt). `privilegedReason` chỉ là cụm ngắn
 * như "Đang là Admin", "Xin vào phòng QL Vận Hành" (staff-access.decision.ts → claimPrivilegeReason),
 * nên luôn ghép thêm câu giải thích, không hiện trơn.
 */
export const PRIVILEGED_WHO = 'Admin, Quản lý, hoặc người xin vào / đang ở phòng QL Vận Hành';
export const PRIVILEGED_GO = 'Đổi phòng ban của họ ở trang Tài khoản.';

const privilegedShort = (reason: unknown) => warningText(reason).replace(/[.\s]+$/, '');

/** Vd. "Xin vào phòng QL Vận Hành: không duyệt ở đây vì liên quan cấp duyệt." */
export const privilegedWhy = (reason: unknown) =>
  `${privilegedShort(reason) || PRIVILEGED_WHO}: không duyệt ở đây vì liên quan cấp duyệt.`;

/** Tên kèm lý do cho hộp duyệt hàng loạt, vd. "Lan (xin vào phòng QL Vận Hành)". */
export function privilegedName(row: { user: StaffUserBrief; privilegedReason?: string | null }) {
  const short = privilegedShort(row.privilegedReason);
  const name = personName(row.user);
  return short ? `${name} (${short.charAt(0).toLowerCase()}${short.slice(1)})` : name;
}

/**
 * Báo kết quả hàng loạt. Có người chưa làm được, hoặc làm xong nhưng còn điều cần xem lại
 * (`warnings`), thì liệt kê từng người kèm lý do. `note`: câu thêm ở cuối (vd. ai không được gửi).
 */
export function showBulkResult(
  result: StaffBulkResult,
  verb: string,
  nameOf: (id: number) => string,
  note?: React.ReactNode
) {
  const ok = result.ok.length;
  const failed = result.failed;
  const warnings = result.warnings.filter((warning) => warningText(warning?.text));
  const noteBlock = note ? <p className={styles.resultNote}>{note}</p> : null;
  if (!failed.length && !warnings.length) {
    Modal.success({ title: `${verb} ${ok} người.`, okText: 'Đóng', content: noteBlock });
    return;
  }
  const title = [
    `${verb} ${ok} người`,
    failed.length ? `${failed.length} người chưa làm được` : '',
    warnings.length ? `${warnings.length} người cần xem lại` : '',
  ]
    .filter(Boolean)
    .join(', ');
  Modal.warning({
    title,
    width: 560,
    okText: 'Đóng',
    content: (
      <>
        {failed.length > 0 && (
          <>
            {warnings.length > 0 && <p className={styles.resultHead}>Chưa làm được:</p>}
            <ul className={styles.failures}>
              {failed.map((failure) => (
                <li key={`failed-${failure.id}`}>
                  <strong>{nameOf(failure.id)}</strong>: {failureText(failure)}
                </li>
              ))}
            </ul>
          </>
        )}
        {warnings.length > 0 && (
          <>
            <p className={styles.resultHead}>Đã làm xong nhưng cần xem lại:</p>
            <ul className={styles.failures}>
              {warnings.map((warning) => (
                <li key={`warning-${warning.id}`}>
                  <strong>{nameOf(warning.id)}</strong>: {warningText(warning.text)}
                </li>
              ))}
            </ul>
          </>
        )}
        {noteBlock}
      </>
    ),
  });
}
